/** Surface-area arithmetic for the sales calculator — pure, metres in, m² out. */

export interface Rect {
  length: number;
  width: number;
}

export interface Opening {
  label: string;
  width: number;
  height: number;
  count: number;
}

export interface WallInput {
  /** Room length and width (m): perimeter = 2 × (L + W). Ignored when `perimeter` is set. */
  length: number;
  width: number;
  /** Explicit perimeter (m), for L-shaped or irregular rooms. */
  perimeter: number | null;
  height: number;
  openings: Opening[];
}

const r2 = (x: number) => Math.round((x + Number.EPSILON) * 100) / 100;
const pos = (x: number) => (Number.isFinite(x) && x > 0 ? x : 0);

/** Floor: sum of rectangles (an L-shaped room is two rectangles). */
export function floorArea(rects: Rect[]): number {
  return r2(rects.reduce((acc, r) => acc + pos(r.length) * pos(r.width), 0));
}

export function perimeterOf(i: Pick<WallInput, "length" | "width" | "perimeter">): number {
  return i.perimeter != null && i.perimeter > 0 ? pos(i.perimeter) : 2 * (pos(i.length) + pos(i.width));
}

export function openingsArea(openings: Opening[]): number {
  return r2(openings.reduce((acc, o) => acc + pos(o.width) * pos(o.height) * Math.max(0, Math.floor(pos(o.count))), 0));
}

/** Wall: perimeter × height, minus doors and windows. Never below zero. */
export function wallArea(i: WallInput): { gross: number; openings: number; net: number; perimeter: number } {
  const perimeter = r2(perimeterOf(i));
  const gross = r2(perimeter * pos(i.height));
  const openings = openingsArea(i.openings);
  return { perimeter, gross, openings, net: r2(Math.max(0, gross - openings)) };
}
